import { FilePen, FileUp, Globe2, MessageCircleQuestion, NotebookPen, SquarePlus } from "lucide-react";
import { useEffect, useRef, useState, type ComponentType, type CSSProperties } from "react";

import { useFocusTrap } from "@/hooks/use-focus-trap";
import { useUpload } from "@/hooks/use-upload";
import { createMarkdownNote } from "@/lib/create-note";
import { cn } from "@/lib/utils";
import { uploadAccept } from "@/lib/upload-accept";
import { NODE_COLOR } from "@/lib/palette";
import { useShell } from "@/stores/shell";
import { useTabs } from "@/stores/tabs";

/** The mobile "+" action sheet — a bottom sheet of ways to add to the atlas,
 * mirroring the desktop double-click quick-add. "Add file" opens the native
 * picker, which on phones surfaces the Files app / iCloud Drive / Google Drive /
 * Dropbox, so the studio reads straight from the device's file system. */
function SheetItem({
  icon: Icon,
  tint,
  tintColor,
  label,
  hint,
  onClick,
}: {
  icon: ComponentType<{ className?: string; style?: CSSProperties }>;
  tint?: string;
  /** Inline icon color for a fixed categorical accent that isn't a token
   * (e.g. the org-node signal green from the palette). Takes precedence over
   * `tint` when set. */
  tintColor?: string;
  label: string;
  hint?: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors hover:bg-accent active:bg-accent"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-border bg-muted">
        <Icon
          className={cn("h-[18px] w-[18px]", tint)}
          style={tintColor ? { color: tintColor } : undefined}
        />
      </span>
      <span className="min-w-0">
        <span className="block text-[14px] text-foreground">{label}</span>
        {hint && <span className="block truncate text-[11px] text-muted-foreground">{hint}</span>}
      </span>
    </button>
  );
}

export function MobileAddSheet({
  open,
  onClose,
  onAddWebsite,
}: {
  open: boolean;
  onClose: () => void;
  onAddWebsite: () => void;
}) {
  const upload = useUpload();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  useFocusTrap(sheetRef, open);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const r = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(r);
    }
    setShown(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!mounted) return null;

  // Run an action then dismiss the sheet (file picking dismisses on change so
  // the hidden input survives long enough to open the OS picker).
  const run = (fn: () => void) => {
    fn();
    onClose();
  };

  return (
    <>
      <div
        aria-hidden
        onClick={onClose}
        className={cn(
          "fixed inset-0 z-[60] bg-black/55 backdrop-blur-[2px] transition-opacity duration-300",
          shown ? "opacity-100" : "opacity-0",
        )}
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label="Add to the atlas"
        onTransitionEnd={() => {
          if (!shown) setMounted(false);
        }}
        style={{ transform: shown ? "translateY(0)" : "translateY(100%)" }}
        className="fixed inset-x-0 bottom-0 z-[61] rounded-t-2xl border-t border-border bg-card pb-safe shadow-deep transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
      >
        <div className="mx-auto mt-2.5 h-1 w-10 rounded-full bg-border" />
        <p className="px-5 pb-1 pt-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Add to the atlas
        </p>
        <p className="px-5 pb-1 text-[11px] leading-snug text-muted-foreground">
          Avoid classified, medical, credential, or other highly sensitive material —
          content is processed by third-party AI providers.
        </p>
        <div className="p-2 pb-3">
          <SheetItem
            icon={FileUp}
            tint="text-primary"
            label="Add file…"
            hint="from your phone, iCloud Drive, or Google Drive"
            onClick={() => fileRef.current?.click()}
          />
          <SheetItem
            icon={Globe2}
            tint="text-skyb"
            label="Add website…"
            hint="paste any link to chart it"
            onClick={() => run(onAddWebsite)}
          />
          <SheetItem
            icon={NotebookPen}
            tint="text-primary"
            label="New handwritten note…"
            hint="pages or infinite, ruled, grid or dots, any paper colour"
            onClick={() => run(() => useShell.getState().requestCreate("note"))}
          />
          <SheetItem
            icon={FilePen}
            tint="text-primary"
            label="New markdown…"
            hint="a rich-text note you can format and edit"
            onClick={() => run(() => void createMarkdownNote())}
          />
          <SheetItem
            icon={SquarePlus}
            tintColor={NODE_COLOR.org}
            label="New org node…"
            onClick={() =>
              run(() => {
                useTabs.getState().activate("graph");
                useShell.getState().requestCreate("org");
              })
            }
          />
          <SheetItem
            icon={MessageCircleQuestion}
            tint="text-muted-foreground"
            label="Ask the atlas"
            onClick={() => run(() => useTabs.getState().openAsk())}
          />
        </div>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept={uploadAccept()}
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) void upload(e.target.files);
            e.target.value = "";
            onClose();
          }}
        />
      </div>
    </>
  );
}
